import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, Trophy, Award, Loader2,
  Twitter, Share2, Download, Link as LinkIcon, MessageCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { SPORT_TILES, SPORT_TREND, formatStat } from '@/lib/sportStats';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STORAGE_PUBLIC_BASE = `${API_URL}/api/files`;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

/** Tiny inline SVG sparkline — no chart library needed. */
function Sparkline({ values, width = 320, height = 56, color = '#F59E0B' }) {
  if (!values || values.length === 0) return null;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = Math.max(1, max - min);
  const stepX = values.length > 1 ? width / (values.length - 1) : 0;
  const points = values.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / span) * (height - 8) - 4;
    return `${x},${y}`;
  });
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" points={points.join(' ')} />
      {values.map((v, i) => (
        <circle key={i} cx={i * stepX} cy={height - ((v - min) / span) * (height - 8) - 4}
          r="3" fill={color} />
      ))}
    </svg>
  );
}

const StatBox = ({ label, value, sub }) => (
  <div className="flex flex-col items-center justify-center bg-white/5 border border-white/10 rounded-lg px-3 py-3">
    <div className="text-xs uppercase tracking-[0.2em] text-white/60 font-semibold">{label}</div>
    <div className="text-3xl sm:text-4xl font-black text-white mt-1">{value}</div>
    {sub != null && <div className="text-xs text-white/50 mt-0.5">{sub}</div>}
  </div>
);

export default function CareerStatSheet() {
  const { shareToken } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const team = searchParams.get('team') || '';
  const name = searchParams.get('name') || '';
  const seasonId = searchParams.get('season_id') || '';
  const eventId = searchParams.get('event_id') || '';

  // React Router stamps `key='default'` on the initial in-app entry. When that
  // is true the user deep-linked here (shared URL, fresh tab) and there's no
  // prior SPA page to go back to — fall back to the public leaderboard.
  // Otherwise navigate(-1) hops back to wherever they came from
  // (Shared Schedule's Season Stats tab, Public Team Detail page, the
  // leaderboard itself, the StatsHub, etc).
  const hasHistory = location.key !== 'default';
  const fallbackHref = `/stats/leaderboard/${shareToken}${seasonId ? `?season_id=${seasonId}` : ''}`;
  const handleBack = (e) => {
    if (hasHistory) {
      e.preventDefault();
      navigate(-1);
    }
    // else: <Link> default href navigation runs.
  };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams({ team, name });
    if (seasonId) params.append('season_id', seasonId);
    if (eventId) params.append('event_id', eventId);
    setLoading(true);
    fetch(`${API_URL}/api/stats/leaderboard/${shareToken}/career?${params.toString()}`)
      .then(r => { if (!r.ok) throw new Error('Player not found on this leaderboard'); return r.json(); })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [shareToken, team, name, seasonId, eventId]);

  const handleShare = async () => {
    const url = window.location.href;
    const title = `${data?.player?.name || name} — Career Stats`;
    const cardParams = new URLSearchParams({ team, name });
    if (seasonId) cardParams.append('season_id', seasonId);
    if (eventId) cardParams.append('event_id', eventId);
    const cardUrl = `${API_URL}/api/stats/leaderboard/${shareToken}/career/card.png?${cardParams.toString()}`;

    // Try to fetch the rendered PNG and share it as a file (the highest-quality
    // experience on mobile — drops the user straight into Instagram/X/WhatsApp).
    try {
      const res = await fetch(cardUrl);
      if (res.ok) {
        const blob = await res.blob();
        const file = new File([blob], `${(data?.player?.name || name).replace(/\s+/g, '-')}-card.png`, { type: 'image/png' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({ files: [file], title, text: url });
            return;
          } catch (err) {
            if (err && err.name === 'AbortError') return;
          }
        }
      }
    } catch {
      // fall through to link-only sharing
    }

    // 1. Native share sheet (link only)
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (err) {
        if (err && err.name === 'AbortError') return;
      }
    }
    // Fall back to the explicit copy-link flow.
    await handleCopyLink();
  };

  // Build a short, social-ready caption from the loaded player data. Falls
  // back to the sport's primary stats when we don't have basketball data
  // (so a volleyball career caption reads e.g.:
  //   "Marcus Reed · 42 Kills · 18 Blocks · Thunder Spikers · Summer League")
  const buildCaption = () => {
    const p = data?.player || {};
    const a = data?.averages || {};
    const st = data?.season_totals || {};
    const sp = data?.sport || 'basketball';
    const orgLabel = (data?.branding?.subtitle || data?.branding?.title || '').trim();
    const stats = [];
    if (sp === 'basketball') {
      if (a.pts != null) stats.push(`${a.pts} PPG`);
      if (a.reb != null) stats.push(`${a.reb} RPG`);
      if (a.ast != null) stats.push(`${a.ast} APG`);
    } else if (sp === 'volleyball') {
      if (st.kills) stats.push(`${st.kills} Kills`);
      if (st.blocks) stats.push(`${st.blocks} Blocks`);
      if (st.aces) stats.push(`${st.aces} Aces`);
    } else if (sp === 'soccer') {
      if (st.goals) stats.push(`${st.goals} Goals`);
      if (st.assists) stats.push(`${st.assists} Assists`);
      if (st.shots) stats.push(`${st.shots} Shots`);
    } else if (sp === 'netball') {
      if (st.goals) stats.push(`${st.goals} Goals`);
      if (st.assists) stats.push(`${st.assists} Assists`);
      if (st.intercepts) stats.push(`${st.intercepts} Intercepts`);
    } else {
      (SPORT_TILES[sp] || []).forEach((t) => {
        if (st[t.key]) stats.push(`${formatStat(t.key, st[t.key])} ${t.name || t.label}`);
      });
    }
    const parts = [p.name || name];
    if (stats.length) parts.push(stats.slice(0, 3).join(' · '));
    if (p.team_name) parts.push(p.team_name);
    if (orgLabel) parts.push(orgLabel);
    return parts.filter(Boolean).join(' · ');
  };

  const handleShareTwitter = () => {
    const caption = buildCaption();
    const url = window.location.href;
    const intent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}&url=${encodeURIComponent(url)}`;
    window.open(intent, '_blank', 'noopener,noreferrer,width=600,height=600');
  };

  const handleShareWhatsApp = () => {
    // WhatsApp's universal share intent (`wa.me`) accepts a single `text`
    // param. Combining caption + URL with a newline puts the URL on its own
    // line so WhatsApp auto-renders the link preview using our OG promo image.
    const caption = buildCaption();
    const url = window.location.href;
    const text = `${caption}\n${url}`;
    const intent = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(intent, '_blank', 'noopener,noreferrer');
  };

  const handleCopyLink = async () => {
    const url = window.location.href;
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard');
        return;
      } catch {
        // fall through
      }
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) { toast.success('Link copied to clipboard'); return; }
    } catch { /* last resort */ }
    toast(`Copy this link: ${url}`, { duration: 8000 });
  };

  const handleDownloadCard = async () => {
    const cardParams = new URLSearchParams({ team, name });
    if (seasonId) cardParams.append('season_id', seasonId);
    if (eventId) cardParams.append('event_id', eventId);
    const cardUrl = `${API_URL}/api/stats/leaderboard/${shareToken}/career/card.png?${cardParams.toString()}`;
    try {
      const res = await fetch(cardUrl);
      if (!res.ok) throw new Error('Card render failed');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${(data?.player?.name || name).replace(/\s+/g, '-')}-career-card.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      toast.success('Card downloaded');
    } catch {
      toast.error('Could not download card');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center text-center px-4">
        <div>
          <Trophy className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400 text-lg">{error || 'Could not load this career sheet.'}</p>
          <Link
            to={fallbackHref}
            onClick={handleBack}
            className="text-amber-400 text-sm hover:underline mt-4 inline-flex items-center gap-1.5"
            data-testid="career-error-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> {hasHistory ? 'Back' : 'Back to leaderboard'}
          </Link>
        </div>
      </div>
    );
  }

  const { branding = {}, player = {}, games_played = 0, record = {}, season_totals = {}, averages = {}, career_highs = {}, game_log = [], sport = 'basketball', stat_labels = {} } = data;
  const accent = branding.accent_color || '#F59E0B';
  const isBasketball = !sport || sport === 'basketball';
  // Sport-aware headline tiles (season totals) for non-basketball sports.
  const nonBBTiles = SPORT_TILES[sport] || [];
  const nonBBTileValues = nonBBTiles.map((t) => ({
    ...t,
    value: formatStat(t.key, season_totals?.[t.key] ?? 0),
  }));
  // League logo shown in the upper-right corner. Prefer the event-level logo
  // (per-league crest) over the org logo or the custom leaderboard logo URL.
  const leagueLogoSrc = branding.event_logo_path
    ? `${STORAGE_PUBLIC_BASE}/${branding.event_logo_path}`
    : (branding.logo_url || (branding.org_logo_path ? `${STORAGE_PUBLIC_BASE}/${branding.org_logo_path}` : ''));
  // Trend sparkline follows the sport's headline stat (points for basketball).
  const [trendKey, trendLabel] = isBasketball ? ['pts', 'Points'] : (SPORT_TREND[sport] || ['pts', 'Points']);
  const ptsTrend = game_log.map((g) => (isBasketball ? (g?.[trendKey] ?? 0) : (g?.stats?.[trendKey] ?? 0)));
  const headshotUrl = player.headshot_path
    ? (player.headshot_path.startsWith('http') ? player.headshot_path : `${STORAGE_PUBLIC_BASE}/${player.headshot_path}`)
    : null;
  const teamLogoUrl = player.team_logo
    ? (player.team_logo.startsWith('http') ? player.team_logo : `${STORAGE_PUBLIC_BASE}/${player.team_logo}`)
    : null;

  return (
    <div className="min-h-screen bg-slate-900 text-white overflow-x-hidden">
      {/* Top bar */}
      <header className="bg-[#00247D] border-b border-white/10">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 py-3 flex items-center justify-between gap-2">
          <Link to={fallbackHref} onClick={handleBack}
            className="text-white/70 hover:text-white text-sm flex items-center gap-2 shrink-0 min-w-0" data-testid="career-back-link">
            <ArrowLeft className="w-4 h-4 shrink-0" />
            <span className="hidden sm:inline truncate">{hasHistory ? 'Back' : 'Back to leaderboard'}</span>
            <span className="sm:hidden">Back</span>
          </Link>
          <div className="flex items-center gap-1" data-testid="career-share-toolbar">
            <Button size="sm" variant="ghost" onClick={handleShareTwitter}
              className="text-white/70 hover:text-white hover:bg-white/10 px-2 sm:px-3"
              data-testid="career-share-twitter-btn"
              title="Share to X (Twitter)">
              <Twitter className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Post to X</span>
            </Button>
            <Button size="sm" variant="ghost" onClick={handleShareWhatsApp}
              className="text-white/70 hover:text-emerald-300 hover:bg-emerald-500/10 px-2 sm:px-3"
              data-testid="career-share-whatsapp-btn"
              title="Share via WhatsApp">
              <MessageCircle className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">WhatsApp</span>
            </Button>
            <Button size="sm" variant="ghost" onClick={handleShare}
              className="text-white/70 hover:text-white hover:bg-white/10 px-2 sm:px-3"
              data-testid="career-share-btn"
              title="Open native share sheet (image + link)">
              <Share2 className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Share Card</span>
            </Button>
            <Button size="sm" variant="ghost" onClick={handleCopyLink}
              className="text-white/70 hover:text-white hover:bg-white/10 px-2 sm:px-3"
              data-testid="career-copy-link-btn"
              title="Copy a link to this stat sheet">
              <LinkIcon className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Copy Link</span>
            </Button>
            <Button size="sm" variant="ghost" onClick={handleDownloadCard}
              className="text-white/70 hover:text-white hover:bg-white/10 px-2 sm:px-3"
              data-testid="career-download-card-btn"
              title="Download 1080×1080 PNG card">
              <Download className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Download</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#00247D] to-slate-900 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12 relative">
          {leagueLogoSrc && (
            <img
              src={leagueLogoSrc}
              alt={branding.event_name || branding.title || 'League'}
              className="absolute top-4 right-4 sm:top-6 sm:right-6 w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-white/20 bg-white/10 shadow-lg"
              data-testid="career-league-logo"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            <div className="relative">
              {headshotUrl ? (
                <img src={headshotUrl} alt={player.name}
                  className="w-28 h-28 sm:w-36 sm:h-36 rounded-full object-cover border-4 shadow-xl"
                  style={{ borderColor: accent }} data-testid="career-headshot" />
              ) : (
                <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full flex items-center justify-center border-4 shadow-xl text-5xl font-black"
                  style={{ borderColor: accent, backgroundColor: '#0F172A', color: accent }}>
                  {(player.name || '?').charAt(0)}
                </div>
              )}
              {teamLogoUrl && (
                <img src={teamLogoUrl} alt={player.team_name}
                  className="absolute -bottom-1 -right-1 w-12 h-12 rounded-full object-cover border-2 border-slate-900 bg-white" />
              )}
            </div>
            <div className="flex-1 text-center sm:text-left">
              <p className="text-sm uppercase tracking-[0.3em] text-white/70 font-semibold">
                {player.team_name}{player.jersey ? ` · #${player.jersey}` : ''}
              </p>
              <h1 className="text-4xl sm:text-6xl font-black mt-2 break-words leading-none"
                style={{ fontFamily: 'Barlow Condensed', color: accent }} data-testid="career-player-name">
                {player.name?.toUpperCase()}
              </h1>
              <p className="text-base text-white/70 mt-3">
                {games_played} game{games_played !== 1 ? 's' : ''} ·
                <span className="text-emerald-400 ml-1 font-semibold">{record.w}W</span>
                <span className="text-rose-400 ml-1 font-semibold">{record.l}L</span>
                {record.t > 0 && <span className="text-white/60 ml-1 font-semibold">{record.t}T</span>}
                {branding.title && <span className="text-white/60"> · {branding.title}</span>}
              </p>

              {/* Averages strip */}
              {isBasketball ? (
                <div className="grid grid-cols-3 sm:grid-cols-7 gap-2 mt-5" data-testid="career-stat-tiles-basketball">
                  <StatBox label="PPG" value={averages.pts ?? 0} />
                  <StatBox label="RPG" value={averages.reb ?? 0} />
                  <StatBox label="APG" value={averages.ast ?? 0} />
                  <StatBox label="SPG" value={averages.stl ?? 0} />
                  <StatBox label="BPG" value={averages.blk ?? 0} />
                  <StatBox label="TOPG" value={averages.to ?? 0} />
                  <StatBox label="PER" value={data.per ?? 0} />
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-5" data-testid={`career-stat-tiles-${sport}`}>
                  {nonBBTileValues.map((t) => (
                    <StatBox key={t.key} label={t.label} value={t.value} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8">

        {/* Primary-stat trend */}
        {ptsTrend.length > 1 && (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm uppercase tracking-[0.2em] text-white/60 font-bold">{trendLabel} Trend</h3>
                <span className="text-xs text-white/40">last {ptsTrend.length} games</span>
              </div>
              <div className="overflow-x-auto">
                <Sparkline values={ptsTrend} width={Math.max(320, 40 * ptsTrend.length)} color={accent} />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Career highs */}
        {Object.keys(career_highs).length > 0 && (
          <section data-testid="season-highs-section">
            <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-amber-400 mb-3" style={{ fontFamily: 'Barlow Condensed' }}>Season Highs</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {Object.entries(career_highs).map(([k, h]) => (
                <div key={k} className="bg-white/5 border border-white/10 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-wide">
                    <Award className="w-3.5 h-3.5" />
                    <span>{h.label}</span>
                  </div>
                  <div className="text-3xl font-black mt-1" style={{ color: accent }}>{h.value}</div>
                  <div className="text-[11px] text-white/50 mt-1 truncate">vs {h.opponent} · {formatDate(h.date)}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Game log */}
        <section data-testid="career-game-log">
          <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-amber-400 mb-3" style={{ fontFamily: 'Barlow Condensed' }}>Game Log</h2>
          {game_log.length === 0 ? (
            <div className="bg-white/5 border border-white/10 rounded-lg p-8 text-center text-white/60 text-sm" data-testid="career-empty-game-log">
              No games played yet — stats will appear here after this player's first completed game.
            </div>
          ) : isBasketball ? (
          <div className="bg-white/5 border border-white/10 rounded-lg overflow-x-auto">
            <Table className="w-full text-sm">
              <TableHeader>
                <TableRow className="text-white/50 text-xs uppercase tracking-wide hover:bg-transparent border-white/10">
                  <TableHead className="text-left px-3 py-2 text-white/50">Date</TableHead>
                  <TableHead className="text-left px-3 py-2 text-white/50">Opponent</TableHead>
                  <TableHead className="text-center px-2 py-2 text-white/50">Result</TableHead>
                  <TableHead className="text-right px-2 py-2 text-white/50">PTS</TableHead>
                  <TableHead className="text-right px-2 py-2 text-white/50">REB</TableHead>
                  <TableHead className="text-right px-2 py-2 text-white/50">AST</TableHead>
                  <TableHead className="text-right px-2 py-2 text-white/50">STL</TableHead>
                  <TableHead className="text-right px-2 py-2 text-white/50">BLK</TableHead>
                  <TableHead className="text-right px-2 py-2 text-white/50">TO</TableHead>
                  <TableHead className="text-right px-2 py-2 text-white/50">FG</TableHead>
                  <TableHead className="text-right px-2 py-2 text-white/50">3P</TableHead>
                  <TableHead className="text-right px-2 py-2 text-white/50">FT</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {game_log.map((g, idx) => (
                  <TableRow key={idx} className="border-t border-white/10 hover:bg-white/5">
                    <TableCell className="px-3 py-2 whitespace-nowrap text-white/70">{formatDate(g.date)}</TableCell>
                    <TableCell className="px-3 py-2 truncate max-w-[160px]">{g.opponent}</TableCell>
                    <TableCell className="text-center px-2 py-2">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${g.result === 'W' ? 'bg-emerald-500/20 text-emerald-300' : g.result === 'L' ? 'bg-rose-500/20 text-rose-300' : 'bg-white/10 text-white/60'}`}>
                        {g.result} {g.team_score}-{g.opp_score}
                      </span>
                    </TableCell>
                    <TableCell className="text-right px-2 py-2 font-semibold text-white">{g.pts}</TableCell>
                    <TableCell className="text-right px-2 py-2 text-white/80">{g.reb}</TableCell>
                    <TableCell className="text-right px-2 py-2 text-white/80">{g.ast}</TableCell>
                    <TableCell className="text-right px-2 py-2 text-white/60">{g.stl}</TableCell>
                    <TableCell className="text-right px-2 py-2 text-white/60">{g.blk}</TableCell>
                    <TableCell className="text-right px-2 py-2 text-rose-300/80">{g.to}</TableCell>
                    <TableCell className="text-right px-2 py-2 text-white/60">{g.fgm}-{g.fga}</TableCell>
                    <TableCell className="text-right px-2 py-2 text-white/60">{g.three_m}-{g.three_a}</TableCell>
                    <TableCell className="text-right px-2 py-2 text-white/60">{g.ftm}-{g.fta}</TableCell>
                  </TableRow>
                ))}
                {/* Season totals row */}
                <TableRow className="border-t-2 border-white/30 bg-white/5 font-semibold hover:bg-white/5">
                  <TableCell className="px-3 py-2 text-amber-400 uppercase text-[11px] tracking-wide" colSpan={3}>Totals</TableCell>
                  <TableCell className="text-right px-2 py-2 text-white">{season_totals.pts}</TableCell>
                  <TableCell className="text-right px-2 py-2 text-white/90">{season_totals.reb}</TableCell>
                  <TableCell className="text-right px-2 py-2 text-white/90">{season_totals.ast}</TableCell>
                  <TableCell className="text-right px-2 py-2 text-white/70">{season_totals.stl}</TableCell>
                  <TableCell className="text-right px-2 py-2 text-white/70">{season_totals.blk}</TableCell>
                  <TableCell className="text-right px-2 py-2 text-rose-300/80">{season_totals.to}</TableCell>
                  <TableCell className="text-right px-2 py-2 text-white/70">{season_totals.fgm}-{season_totals.fga}</TableCell>
                  <TableCell className="text-right px-2 py-2 text-white/70">{season_totals.three_m}-{season_totals.three_a}</TableCell>
                  <TableCell className="text-right px-2 py-2 text-white/70">{season_totals.ftm}-{season_totals.fta}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          ) : (
          <div className="bg-white/5 border border-white/10 rounded-lg overflow-x-auto" data-testid={`career-game-log-${sport}`}>
            <Table className="w-full text-sm">
              <TableHeader>
                <TableRow className="text-white/50 text-xs uppercase tracking-wide hover:bg-transparent border-white/10">
                  <TableHead className="text-left px-3 py-2 text-white/50">Date</TableHead>
                  <TableHead className="text-left px-3 py-2 text-white/50">Opponent</TableHead>
                  <TableHead className="text-center px-2 py-2 text-white/50">Result</TableHead>
                  {nonBBTiles.map((t) => (
                    <TableHead key={t.key} className="text-right px-2 py-2 text-white/50">{t.label}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {game_log.map((g, idx) => (
                  <TableRow key={idx} className="border-t border-white/10 hover:bg-white/5">
                    <TableCell className="px-3 py-2 whitespace-nowrap text-white/70">{formatDate(g.date)}</TableCell>
                    <TableCell className="px-3 py-2 truncate max-w-[160px]">{g.opponent}</TableCell>
                    <TableCell className="text-center px-2 py-2">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold whitespace-nowrap ${g.result === 'W' ? 'bg-emerald-500/20 text-emerald-300' : g.result === 'L' ? 'bg-rose-500/20 text-rose-300' : 'bg-white/10 text-white/60'}`}
                        data-testid={`career-game-score-${idx}`}>
                        {g.result} {g.score_line || `${g.team_score}-${g.opp_score}`}
                      </span>
                    </TableCell>
                    {nonBBTiles.map((t, ti) => (
                      <TableCell
                        key={t.key}
                        className={`text-right px-2 py-2 ${ti === 0 ? 'font-semibold text-white' : 'text-white/80'}`}
                      >
                        {formatStat(t.key, g?.stats?.[t.key] ?? 0)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {/* Season totals row */}
                <TableRow className="border-t-2 border-white/30 bg-white/5 font-semibold hover:bg-white/5">
                  <TableCell className="px-3 py-2 text-amber-400 uppercase text-[11px] tracking-wide" colSpan={3}>Totals</TableCell>
                  {nonBBTiles.map((t, ti) => (
                    <TableCell
                      key={t.key}
                      className={`text-right px-2 py-2 ${ti === 0 ? 'text-white' : 'text-white/90'}`}
                    >
                      {formatStat(t.key, season_totals?.[t.key] ?? 0)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </div>
          )}
        </section>

        {/* Branded footer */}
        <div className="text-center text-xs text-white/40 pt-6 pb-2 border-t border-white/10">
          <p>Powered by <Link to="/" className="text-amber-400 hover:underline">scheduleguru.ws</Link></p>
        </div>
      </div>
    </div>
  );
}
